import * as React from 'react';
import { memo } from 'react';

import styled from 'styled-components';

import { Menu } from 'organisms';

import { Colors } from 'services/ColorService';

export interface IMainTemplate {
  children: React.ReactElement;
  /**
   * Full-bleed, full-viewport-height variant: drops the 1312px content cap and
   * lets the child fill the space under the menu (used by the Communicator so
   * it can render as an edge-to-edge, full-height messenger). Off by default —
   * every other page keeps the centered, capped layout.
   */
  fluid?: boolean;
}

export const MainTemplate = memo(({ children, fluid }: IMainTemplate) => (
  <Paper $fluid={fluid}>
    <MenuWrapper>
      <MenuMargin $fluid={fluid}>
        <Menu />
      </MenuMargin>
    </MenuWrapper>
    <ContentWrapper $fluid={fluid}>{children}</ContentWrapper>
  </Paper>
));

/**
 * Лист страницы.
 *
 * 🔴 Нижнего зазора здесь НЕТ и быть не должно — владелец сказал прямо. Раньше
 * его добавлял каждый список сам: девять таблиц несли `margin-bottom: 60px`,
 * четыре страницы — `margin: 32px 0 64px`, плюс финансы, опросы и Zoom. Все
 * они убраны, и заводить общий взамен не надо: страница кончается там, где
 * кончается содержимое.
 *
 * Почему это было не только некрасиво: зазор лежал ВНУТРИ листа, а у листа
 * `min-height: 100vh`. Стоило содержимому дорасти до нижней кромки экрана, как
 * 60 пикселей пустоты выталкивали документ за 100vh — появлялась полоса
 * прокрутки, под которой ничего нет.
 *
 * `box-sizing: border-box` оставлен намеренно: если сюда когда-нибудь вернут
 * padding, он будет считаться ВНУТРИ 100vh и той же полосы прокрутки не даст.
 */
export const Paper = styled.div<{ $fluid?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: ${({ $fluid }) => ($fluid ? '100vh' : 'auto')};
  min-height: 100vh;
  overflow: ${({ $fluid }) => ($fluid ? 'hidden' : 'visible')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: ${Colors.MainBackground};
`;

const MenuMargin = styled.div<{ $fluid?: boolean }>`
  max-width: ${({ $fluid }) => ($fluid ? 'none' : '1312px')};
  padding: ${({ $fluid }) => ($fluid ? '0 24px' : '0')};
  box-sizing: border-box;
  margin: 0 auto;
`;

const MenuWrapper = styled.div`
  width: 100%;
  background-color: ${Colors.White};
  border-bottom: 1px solid ${Colors.LightGray};
`;

const ContentWrapper = styled.div<{ $fluid?: boolean }>`
  width: 100%;
  max-width: ${({ $fluid }) => ($fluid ? 'none' : '1312px')};
  flex: ${({ $fluid }) => ($fluid ? '1 1 auto' : '0 1 auto')};
  min-height: ${({ $fluid }) => ($fluid ? '0' : 'auto')};
  min-width: 0;
  display: flex;
  margin: 0 auto;
  flex-direction: column;
  /* Fluid pages must fill the width — a flex-start child would shrink to its
   * content, so a narrow view (e.g. the messenger's thread) would collapse the
   * whole page. Centered pages keep the start alignment. */
  align-items: ${({ $fluid }) => ($fluid ? 'stretch' : 'flex-start')};
  justify-content: flex-start;
  background-color: ${Colors.Transparent};
`;
